import { useState, useEffect } from 'react'
import { AdminLayout } from '../../components/layouts/Layout'
import { Card } from '../../components/common/UI'
import { TrendingUp, Eye, MousePointer, DollarSign } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { advertisementService, creditPurchaseService } from '../../services/api'

export default function Analytics() {
  const [advertisements, setAdvertisements] = useState<any[]>([])
  const [creditPurchases, setCreditPurchases] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [adsData, purchasesData] = await Promise.all([
        advertisementService.list(),
        creditPurchaseService.list()
      ])
      setAdvertisements(adsData)
      setCreditPurchases(purchasesData)
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Loading...</p>
        </div>
      </AdminLayout>
    )
  }

  const totalImpressions = advertisements.reduce((sum: number, ad: any) => sum + (ad.impressionCount || 0), 0)
  const totalClicks = advertisements.reduce((sum: number, ad: any) => sum + (ad.clickCount || 0), 0)
  const totalRevenue = creditPurchases
    .filter((tp: any) => tp.status === 'COMPLETED')
    .reduce((sum: number, tp: any) => sum + tp.amount, 0)
  const avgCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0

  const topAds = [...advertisements]
    .sort((a, b) => (b.impressionCount || 0) - (a.impressionCount || 0))
    .slice(0, 10)

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-gray-600">Platform performance metrics</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Impressions</p>
                <p className="text-2xl font-bold">{formatters.number(totalImpressions)}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Clicks</p>
                <p className="text-2xl font-bold">{formatters.number(totalClicks)}</p>
              </div>
              <MousePointer className="w-8 h-8 text-green-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Average CTR</p>
                <p className="text-2xl font-bold">{avgCTR.toFixed(2)}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold">{formatters.currency(totalRevenue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </Card>
        </div>

        <Card>
          <h3 className="text-lg font-semibold mb-4">Top Performing Ads</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Ad Title</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Business</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Impressions</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Clicks</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">CTR</th>
                </tr>
              </thead>
              <tbody>
                {topAds.map((ad) => (
                  <tr key={ad.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{ad.adTitle}</td>
                    <td className="py-3 px-4">{ad.businessName}</td>
                    <td className="py-3 px-4">{formatters.number(ad.impressionCount || 0)}</td>
                    <td className="py-3 px-4">{formatters.number(ad.clickCount || 0)}</td>
                    <td className="py-3 px-4">
                      {((ad.clickCount || 0) / (ad.impressionCount || 1) * 100).toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>
  )
}